/**
 * Environment Configuration Module
 *
 * Handles environment-specific settings for Build the Stack
 * Automatically configures based on Vite mode (staging, production, development)
 */

export interface EnvironmentConfig {
  /** Current environment name */
  environment: 'development' | 'staging' | 'production'

  /** Whether SEO indexing is enabled */
  enableSeoIndexing: boolean

  /** Base URL for API calls */
  apiBaseUrl: string

  /** Whether analytics should be enabled */
  enableAnalytics: boolean

  /** Whether debug tools should be shown */
  enableDebugTools: boolean

  /** Logging level */
  logLevel: 'error' | 'warn' | 'info' | 'debug'

  /** App metadata */
  app: {
    name: string
    version: string
    author: string
  }
}

/**
 * Get the current environment configuration
 * Uses Vite's import.meta.env and build-time defines
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  // Get environment from Vite mode or define
  const environment = (
    typeof __VITE_ENVIRONMENT__ !== 'undefined'
      ? __VITE_ENVIRONMENT__
      : import.meta.env.MODE
  ) as 'development' | 'staging' | 'production'

  // Get SEO indexing setting
  const enableSeoIndexing =
    typeof __VITE_ENABLE_SEO_INDEXING__ !== 'undefined'
      ? __VITE_ENABLE_SEO_INDEXING__
      : environment === 'production'

  // Get API base URL
  const apiBaseUrl =
    typeof __VITE_API_BASE_URL__ !== 'undefined'
      ? __VITE_API_BASE_URL__
      : environment === 'production'
        ? 'https://lucasstufflebeam.com'
        : 'https://lucasstufflebeam.pages.dev'

  return {
    environment,
    enableSeoIndexing,
    apiBaseUrl,
    enableAnalytics: environment === 'production',
    enableDebugTools: environment !== 'production',
    logLevel: environment === 'production' ? 'error' : 'info',
    app: {
      name: 'Build the Stack',
      version: '1.0.0',
      author: 'Lucas Stufflebeam',
    },
  }
}

/**
 * Environment configuration singleton
 */
export const environmentConfig = getEnvironmentConfig()

/**
 * Utility functions for environment checks
 */
export const isProduction = () => environmentConfig.environment === 'production'
export const isStaging = () => environmentConfig.environment === 'staging'
export const isDevelopment = () =>
  environmentConfig.environment === 'development'

/**
 * SEO Meta tags based on environment
 */
export function getEnvironmentMetaTags(): Array<{
  name: string
  content: string
}> {
  const tags: Array<{ name: string; content: string }> = []

  if (!environmentConfig.enableSeoIndexing) {
    tags.push(
      { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' },
      { name: 'googlebot', content: 'noindex, nofollow' },
      { name: 'staging-environment', content: 'true' }
    )
  } else {
    tags.push({ name: 'robots', content: 'index, follow' })
  }

  return tags
}

/**
 * Console logger that respects environment log level
 */
export const logger = {
  error: (...args: any[]) => console.error('[BUILD-THE-STACK]', ...args),
  warn: (...args: any[]) => {
    if (['warn', 'info', 'debug'].includes(environmentConfig.logLevel)) {
      console.warn('[BUILD-THE-STACK]', ...args)
    }
  },
  info: (...args: any[]) => {
    if (['info', 'debug'].includes(environmentConfig.logLevel)) {
      console.info('[BUILD-THE-STACK]', ...args)
    }
  },
  debug: (...args: any[]) => {
    if (environmentConfig.logLevel === 'debug') {
      console.debug('[BUILD-THE-STACK]', ...args)
    }
  },
}

// Log current environment on module load
logger.info(`Environment: ${environmentConfig.environment}`)
logger.info(
  `SEO Indexing: ${environmentConfig.enableSeoIndexing ? 'enabled' : 'disabled'}`
)
logger.info(`API Base URL: ${environmentConfig.apiBaseUrl}`)

declare global {
  const __VITE_ENVIRONMENT__: string
  const __VITE_ENABLE_SEO_INDEXING__: boolean
  const __VITE_API_BASE_URL__: string
}
